// src/components/ui/accordion.tsx
'use client'

import React from 'react'
import {
  Root as AccordionRoot,
  Item as AccordionItemPrimitive,
  Header as AccordionHeader,
  Trigger as AccordionTriggerPrimitive,
  Content as AccordionContentPrimitive,
} from '@radix-ui/react-accordion'
import { ChevronDown } from 'lucide-react'
import { cn } from '@/lib/utils'

/** Re-export the root so API stays the same */
const Accordion = AccordionRoot

const AccordionItem = React.forwardRef<
  React.ElementRef<typeof AccordionItemPrimitive>,
  React.ComponentPropsWithoutRef<typeof AccordionItemPrimitive>
>(({ className, ...props }, ref) => (
  <AccordionItemPrimitive
    ref={ref}
    className={cn('border-b', className)}
    {...props}
  />
))
AccordionItem.displayName = 'AccordionItem'

const AccordionTrigger = React.forwardRef<
  React.ElementRef<typeof AccordionTriggerPrimitive>,
  React.ComponentPropsWithoutRef<typeof AccordionTriggerPrimitive>
>(({ className, children, ...props }, ref) => (
  <AccordionHeader className="flex">
    <AccordionTriggerPrimitive
      ref={ref}
      className={cn(
        'flex flex-1 items-center justify-between py-4 font-medium transition-transform hover:underline',
        // rotate the chevron when open (transform-only → composited)
        "[&[data-state=open]>svg]:rotate-180",
        className
      )}
      {...props}
    >
      {children}
      <ChevronDown className="h-4 w-4 shrink-0 transition-transform duration-200" aria-hidden="true" />
    </AccordionTriggerPrimitive>
  </AccordionHeader>
))
AccordionTrigger.displayName = 'AccordionTrigger'

const AccordionContent = React.forwardRef<
  React.ElementRef<typeof AccordionContentPrimitive>,
  React.ComponentPropsWithoutRef<typeof AccordionContentPrimitive>
>(({ className, children, ...props }, ref) => (
  <AccordionContentPrimitive
    ref={ref}
    className={cn(
      'overflow-hidden text-sm transition-[height,opacity]',
      // uses your tailwind keyframes from config
      'data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down'
    )}
    {...props}
  >
    <div className={cn('pb-4 pt-0', className)}>{children}</div>
  </AccordionContentPrimitive>
))
AccordionContent.displayName = 'AccordionContent'

export { Accordion, AccordionItem, AccordionTrigger, AccordionContent }